import { useState } from 'react';
import { ChevronDownIcon, MessagesSquareIcon } from 'lucide-react';
import { useNamespaceCopy } from '@/i18n/ns';
import { formatCount } from '@/i18n/format';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import renderKitCopy from '@/i18n/ns/render-kit';
import { EmailMessage, Sender } from './email-message';
import { formatEmailDate, type EmailThreadView } from './email-model';

/** render_email_thread: the conversation, newest message open, earlier ones one line each. */
export function EmailThreadCard({ thread }: { thread: EmailThreadView }) {
  const copy = useNamespaceCopy(renderKitCopy);
  const locale = useChatInterfaceLocale();
  const last = thread.messages.length - 1;
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set([last]));
  const toggle = (index: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  return (
    <article className="pc-email-card" data-renderer="email_thread" aria-label={copy.threadCard}>
      <header className="pc-email-head">
        <MessagesSquareIcon aria-hidden="true" />
        <h3 className="pc-email-subject">{thread.subject || copy.noSubject}</h3>
        <span className="pc-email-count">
          {formatCount(copy.messagesCount, thread.messages.length + thread.omitted, locale)}
        </span>
      </header>
      {thread.omitted > 0 && (
        <p className="pc-email-omitted">
          {formatCount(copy.earlierOmitted, thread.omitted, locale)}
        </p>
      )}
      <ol className="pc-email-thread">
        {thread.messages.map((email, index) => (
          <li key={email.id ?? index} data-open={open.has(index) ? 'true' : undefined}>
            {open.has(index) && index === last ? (
              <EmailMessage email={email} />
            ) : (
              <>
                <button
                  type="button"
                  className="pc-email-row"
                  aria-expanded={open.has(index)}
                  onClick={() => toggle(index)}
                >
                  <Sender value={email.from} />
                  <span className="pc-email-row-snippet">{email.body.slice(0, 160)}</span>
                  {email.date && (
                    <time className="pc-email-date" dateTime={email.date}>
                      {formatEmailDate(email.date, locale)}
                    </time>
                  )}
                  <ChevronDownIcon aria-hidden="true" className="pc-email-row-chevron" />
                </button>
                {open.has(index) && <EmailMessage email={email} />}
              </>
            )}
          </li>
        ))}
      </ol>
    </article>
  );
}
